import { useState } from "react";
import { Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { UserFilterField, UserFilterTag, UserFilterOperator } from "@/hooks/useAdminUsers";
import { useUserTags } from "@/hooks/useUserTags";

interface UserColumnFiltersProps {
  filterTags: UserFilterTag[];
  addFilter: (field: UserFilterField, value: string, operator?: UserFilterOperator) => void;
  removeFilter: (tagId: string) => void;
  clearAllFilters: () => void;
}

const FIELD_OPTIONS: {
  value: UserFilterField;
  label: string;
  placeholder: string;
  options?: { value: string; label: string }[];
  dynamicOptions?: boolean;
  operators?: UserFilterOperator[];
}[] = [
  { value: "name", label: "Name", placeholder: "Search by name...", operators: ["contains", "equals", "length>=", "length<=", "length=", "length>", "length<"] },
  { value: "email", label: "Email", placeholder: "Search by email...", operators: ["contains", "equals", "length>=", "length<=", "length=", "length>", "length<"] },
  { value: "phone", label: "Phone", placeholder: "Search by phone...", operators: ["contains", "equals", "length>=", "length<=", "length=", "length>", "length<"] },
  { value: "userId", label: "User ID", placeholder: "Search by user ID...", operators: ["contains", "equals"] },
  {
    value: "memberType",
    label: "Member Type",
    placeholder: "Select member type",
    options: [
      { value: "member", label: "Member" },
      { value: "host", label: "Host" },
    ],
  },
  {
    value: "role",
    label: "Role",
    placeholder: "Select role",
    options: [
      { value: "admin", label: "Admin" },
      { value: "user", label: "User" },
    ],
  },
  {
    value: "credits",
    label: "Credits",
    placeholder: "Enter amount...",
    operators: ["equals", ">=", "<=", ">", "<"],
  },
  {
    value: "isUnlimited",
    label: "Unlimited",
    placeholder: "Select status",
    options: [
      { value: "active", label: "Active" },
      { value: "expired", label: "Expired" },
      { value: "never", label: "Never" },
    ],
  },
  {
    value: "tag",
    label: "Tag",
    placeholder: "Select tag...",
    dynamicOptions: true,
  },
  {
    value: "eventCount",
    label: "Events",
    placeholder: "Enter count...",
    operators: ["equals", ">=", "<=", ">", "<"],
  },
  {
    value: "inactiveDays",
    label: "Inactive Days",
    placeholder: "Enter days...",
    operators: [">=", "<=", ">", "<"],
  },
  {
    value: "joinedDate",
    label: "Joined",
    placeholder: "YYYY-MM-DD",
    operators: ["equals", ">=", "<=", ">", "<"],
  },
  {
    value: "lastActiveDate",
    label: "Last Active",
    placeholder: "YYYY-MM-DD",
    operators: ["equals", ">=", "<=", ">", "<"],
  },
  {
    value: "lastPurchaseDate",
    label: "Last Purchase",
    placeholder: "YYYY-MM-DD",
    operators: ["equals", ">=", "<=", ">", "<"],
  },
   {
     value: "partnerCode",
     label: "Partner Code",
     placeholder: "Search by code...",
     operators: ["contains", "equals"],
   },
   {
     value: "hasPartnerCode",
     label: "Has Partner Code",
     placeholder: "Select option",
     options: [
       { value: "yes", label: "Yes" },
       { value: "no", label: "No" },
     ],
   },
   {
     value: "referredBy",
     label: "Referred By",
     placeholder: "Search by referrer name...",
     operators: ["contains", "equals"],
   },
   {
     value: "wasReferred",
     label: "Was Referred",
     placeholder: "Select option",
     options: [
       { value: "yes", label: "Yes" },
       { value: "no", label: "No" },
     ],
   },
   {
     value: "referralCount",
     label: "Referrals Made",
     placeholder: "Enter count...",
     operators: ["equals", ">=", "<=", ">", "<"],
   },
];

const FIELD_COLORS: Record<UserFilterField, string> = {
  name: "bg-blue-500/20 text-blue-700 border-blue-500/30",
  email: "bg-purple-500/20 text-purple-700 border-purple-500/30",
  phone: "bg-green-500/20 text-green-700 border-green-500/30",
  userId: "bg-gray-500/20 text-gray-700 border-gray-500/30",
  memberType: "bg-orange-500/20 text-orange-700 border-orange-500/30",
  role: "bg-red-500/20 text-red-700 border-red-500/30",
  credits: "bg-yellow-500/20 text-yellow-700 border-yellow-500/30",
  isUnlimited: "bg-pink-500/20 text-pink-700 border-pink-500/30",
  tag: "bg-violet-500/20 text-violet-700 border-violet-500/30",
  eventCount: "bg-cyan-500/20 text-cyan-700 border-cyan-500/30",
  inactiveDays: "bg-rose-500/20 text-rose-700 border-rose-500/30",
  joinedDate: "bg-teal-500/20 text-teal-700 border-teal-500/30",
  lastActiveDate: "bg-indigo-500/20 text-indigo-700 border-indigo-500/30",
  lastPurchaseDate: "bg-emerald-500/20 text-emerald-700 border-emerald-500/30",
   partnerCode: "bg-fuchsia-500/20 text-fuchsia-700 border-fuchsia-500/30",
   hasPartnerCode: "bg-fuchsia-500/20 text-fuchsia-700 border-fuchsia-500/30",
   referredBy: "bg-sky-500/20 text-sky-700 border-sky-500/30",
   wasReferred: "bg-sky-500/20 text-sky-700 border-sky-500/30",
   referralCount: "bg-lime-500/20 text-lime-700 border-lime-500/30",
};

const OPERATOR_LABELS: Record<UserFilterOperator, string> = {
  contains: "contains",
  equals: "=",
  ">=": "≥",
  "<=": "≤",
  ">": ">",
  "<": "<",
  "length>=": "length ≥",
  "length<=": "length ≤",
  "length=": "length =",
  "length>": "length >",
  "length<": "length <",
};

export const UserColumnFilters = ({
  filterTags,
  addFilter,
  removeFilter,
  clearAllFilters,
}: UserColumnFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<UserFilterField>("name");
  const [selectedOperator, setSelectedOperator] = useState<UserFilterOperator>("contains");
  const [inputValue, setInputValue] = useState("");
  
  const { allTags } = useUserTags();

  const selectedFieldConfig = FIELD_OPTIONS.find((f) => f.value === selectedField);
  
  // Get dynamic options for tags
  const getFieldOptions = () => {
    if (selectedFieldConfig?.dynamicOptions && selectedField === "tag") {
      return allTags.map((tag) => ({ value: tag.name.toLowerCase(), label: tag.name }));
    }
    return selectedFieldConfig?.options;
  };
  
  const fieldOptions = getFieldOptions();
  const hasOptions = !!fieldOptions && fieldOptions.length > 0;
  const hasOperators = !!selectedFieldConfig?.operators && selectedFieldConfig.operators.length > 0;

  const handleAddFilter = () => {
    if (inputValue.trim()) {
      const operator = hasOperators ? selectedOperator : undefined;
      addFilter(selectedField, inputValue.trim(), operator);
      setInputValue("");
      setIsOpen(false);
    }
  };

  const handleSelectOption = (value: string) => {
    addFilter(selectedField, value);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddFilter();
    }
  };

  const handleFieldChange = (field: UserFilterField) => {
    setSelectedField(field);
    setInputValue("");
    const fieldConfig = FIELD_OPTIONS.find((f) => f.value === field);
    if (fieldConfig?.operators?.length) {
      setSelectedOperator(fieldConfig.operators[0]);
    }
  };

  const formatTagDisplay = (tag: UserFilterTag) => {
    if (tag.operator) {
      return `${OPERATOR_LABELS[tag.operator]} ${tag.value}`;
    }
    return tag.value;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filter
            {filterTags.length > 0 && (
              <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                {filterTags.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="end">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Field</span>
              <div className="flex flex-wrap gap-1">
                {FIELD_OPTIONS.map((field) => (
                  <Button
                    key={field.value}
                    variant={selectedField === field.value ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleFieldChange(field.value)}
                  >
                    {field.label}
                  </Button>
                ))}
              </div>
            </div>

            <Separator />

            {hasOperators && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Operator</span>
                <div className="flex flex-wrap gap-1">
                  {selectedFieldConfig?.operators?.map((op) => (
                    <Button
                      key={op}
                      variant={selectedOperator === op ? "default" : "outline"}
                      size="sm"
                      className="h-7 text-xs min-w-[40px]"
                      onClick={() => setSelectedOperator(op)}
                    >
                      {OPERATOR_LABELS[op]}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Value</span>
              {hasOptions ? (
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                  {fieldOptions?.map((option) => (
                    <Button
                      key={option.value}
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleSelectOption(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder={selectedFieldConfig?.placeholder || "Enter value..."}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-8 text-sm"
                    type={["credits", "eventCount", "inactiveDays"].includes(selectedField) ? "number" : "text"}
                  />
                  <Button size="sm" className="h-8" onClick={handleAddFilter}>
                    Add
                  </Button>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {filterTags.map((tag) => (
        <div
          key={tag.id}
          className={cn(
            "flex items-center gap-1 h-7 px-2 rounded-md border text-xs font-medium",
            FIELD_COLORS[tag.field]
          )}
        >
          <span className="opacity-70">{tag.label}:</span>
          <span>{formatTagDisplay(tag)}</span>
          <button
            onClick={() => removeFilter(tag.id)}
            className="ml-0.5 hover:opacity-70 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}

      {filterTags.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAllFilters}
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          Clear all
        </Button>
      )}
    </div>
  );
};
